import { FC, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Typography,
  Grid,
} from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import StorageIcon from '@mui/icons-material/Storage'
import BarChartIcon from '@mui/icons-material/BarChart'
import MapIcon from '@mui/icons-material/Map'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import TuneIcon from '@mui/icons-material/Tune'
import { Title, useTranslate, useGetList } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { GemeenteResponse, PrognoseSetResponse } from '../types/apiRequests'

// ── Animated number counter ─────────────────────────────────────────
const useCountUp = (end: number, duration = 800): number => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (end === 0) { setCount(0); return }
    let start = 0
    const increment = end / (duration / 16)
    const timer = setInterval(() => {
      start += increment
      if (start >= end) { setCount(end); clearInterval(timer) }
      else setCount(Math.floor(start))
    }, 16)
    return () => clearInterval(timer)
  }, [end, duration])
  return count
}

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

// ── Metric card ─────────────────────────────────────────────────────
const MetricCard = styled(Box)<{ accent: string; delay: number }>(({ accent, delay }) => ({
  'position': 'relative',
  'padding': '28px 24px 24px',
  'borderRadius': 16,
  'background': '#fff',
  'border': '1px solid rgba(0,0,0,0.06)',
  'overflow': 'hidden',
  'animation': `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
  'transition': 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: accent,
    borderRadius: '16px 16px 0 0',
  },
}))

interface MetricProps {
  value: string | number
  label: string
  accent: string
  delay?: number
}

const Metric: FC<MetricProps> = ({ value, label, accent, delay = 0 }) => (
  <MetricCard accent={accent} delay={delay}>
    <Typography
      sx={{
        fontSize: 32,
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        color: '#1a1a1a',
        mb: 0.75,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(0,0,0,0.45)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </Typography>
  </MetricCard>
)

// ── Navigation card ─────────────────────────────────────────────────
interface NavCardProps {
  title: string
  description: string
  icon: React.ReactNode
  to: string
  accentColor: string
}

const NavCard: FC<NavCardProps> = ({ title, description, icon, to, accentColor }) => {
  const navigate = useNavigate()

  return (
    <Card
      sx={{
        'borderLeft': `4px solid ${accentColor}`,
        'transition': 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)',
        },
      }}
    >
      <CardActionArea onClick={() => navigate(to)} sx={{ p: 0 }}>
        <CardContent sx={{ 'display': 'flex', 'alignItems': 'center', 'gap': 2, 'py': 1.5, 'px': 2, '&:last-child': { pb: 1.5 } }}>
          <Box
            sx={{
              color: accentColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4 }}>
              {description}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

const getGreeting = (translate: (key: string) => string): string => {
  const hour = new Date().getHours()
  if (hour < 12) return translate('dashboard.greeting.morning')
  if (hour < 18) return translate('dashboard.greeting.afternoon')
  return translate('dashboard.greeting.evening')
}

// ── Main Dashboard ──────────────────────────────────────────────────
const Dashboard: FC = () => {
  const translate = useTranslate()

  const { data: gemeenten, isLoading: gemeentenLoading } = useGetList<GemeenteResponse>('gemeenten', {
    pagination: { page: 1, perPage: 99 },
    sort: { field: 'naam', order: 'ASC' },
  })

  const { data: stadsprognoses, isLoading: stadLoading } = useGetList<PrognoseSetResponse>('prognose_sets', {
    pagination: { page: 1, perPage: 99 },
    sort: { field: 'created', order: 'DESC' },
    filter: { type: 'stad' },
  })

  const { data: gebiedsprognoses, isLoading: gebiedLoading } = useGetList<PrognoseSetResponse>('prognose_sets', {
    pagination: { page: 1, perPage: 99 },
    sort: { field: 'created', order: 'DESC' },
    filter: { type: 'gebied' },
  })

  const loading = gemeentenLoading || stadLoading || gebiedLoading

  const gemeenteCount = useCountUp(gemeenten?.length ?? 0)
  const stadCount = useCountUp(stadsprognoses?.length ?? 0)
  const gebiedCount = useCountUp(gebiedsprognoses?.length ?? 0)

  const activeSprognoses = stadsprognoses?.filter(p => p.publicatie_status === 'gepubliceerd').length ?? 0
  const activeCount = useCountUp(activeSprognoses)

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Title title="Dashboard" />

      {/* Header */}
      <Box sx={{ mb: 4, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Typography sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', fontWeight: 400, mb: 0.5 }}>
          {getGreeting(translate)}
        </Typography>
        <Typography sx={{
          fontSize: { xs: 24, md: 28 },
          fontWeight: 700,
          letterSpacing: '-0.025em',
          color: '#1a1a1a',
          lineHeight: 1.15,
        }}>
          {translate('dashboard.description')}
        </Typography>
      </Box>

      {/* Statistics */}
      {loading ? (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 4,
        }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} sx={{ p: '28px 24px 24px', borderRadius: 4, background: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Skeleton variant="text" width={60} height={40} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={100} height={18} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 4,
        }}>
          <Metric value={gemeenteCount} label="Gemeenten" accent="rgba(49,103,155,1)" delay={0} />
          <Metric value={stadCount} label="Stadsprognoses" accent="rgba(0,115,77,1)" delay={60} />
          <Metric value={gebiedCount} label="Gebiedsprognoses" accent="rgba(232,110,68,1)" delay={120} />
          <Metric value={activeCount} label="Gepubliceerd" accent="rgba(105,162,255,1)" delay={180} />
        </Box>
      )}

      {/* Navigation cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <NavCard
            title="Historische data"
            description="Bevolkingsdata per gemeente. Import CBS data, exporteer naar Excel."
            icon={<StorageIcon sx={{ fontSize: 28 }} />}
            to="/historische_data_stad"
            accentColor="rgba(49,103,155,1)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NavCard
            title="Stadsprognoses"
            description="Demografische prognoses op stadsniveau. Aannames en stuurkaarten."
            icon={<BarChartIcon sx={{ fontSize: 28 }} />}
            to="/prognose_sets"
            accentColor="rgba(0,115,77,1)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NavCard
            title="Gebiedsprognoses"
            description="Bevolking verdelen over gebieden op basis van stadsprognoses."
            icon={<MapIcon sx={{ fontSize: 28 }} />}
            to="/gebiedsprognoses"
            accentColor="rgba(232,110,68,1)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NavCard
            title="Overige data"
            description="Stuurkaarten, woningbouwplanningen, externe prognoses, gebiedsindelingen."
            icon={<TuneIcon sx={{ fontSize: 28 }} />}
            to="/overige_data_stad"
            accentColor="rgba(255,176,62,1)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NavCard
            title="Gemeenten"
            description="Beheer gemeenten en hun configuratie voor prognoses."
            icon={<LocationCityIcon sx={{ fontSize: 28 }} />}
            to="/gemeenten"
            accentColor="rgba(105,162,255,1)"
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard
